/* global MEDIA_ROOT */

import React, { PropTypes } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';

import { getLocalQuery, getCountryViewport } from '../../utils/geoLocation';
import { stringifyQS } from '../../utils';

import ButtonLink from '../ButtonLink';
import Container from '../Container';
import Discover from '../Discover';

import HelpPrompt from '../HelpPrompt';
import MemberBenefits from '../MemberBenefits';
import EventListContainer from '../EventList/EventListContainer.react';
import DiscoverFooter from '../DiscoverFooter';
import SignUpPrompt from '../SignUpPrompt';
import HomepageSearchContainer from '../../containers/HomepageSearch';

import {
  categories,
  header,
  heading,
  partners,
  partnerList,
  popular,
  search,
  section,
  signUpPrompt,
  backgroundContainer,
} from './styles.module.css';

/**
 * Homepage presentational component.
 */
const Homepage = ({ hasUserCookie, geoLocation, trendingQuery, recentEventViews, countryCode }) => (
  <div>
    <header className={header}>
      <div className={backgroundContainer} />
      <Container className={search}>
        {geoLocation && <HomepageSearchContainer geoLocation={geoLocation} />}
      </Container>
      <Container>
        <section className={categories}>
          <Discover showLess countryCode={countryCode} />
        </section>
      </Container>
    </header>

    {recentEventViews && recentEventViews.length &&
    <section className={popular}>
      <Container>
        <h2 className={heading}>Recently viewed</h2>
        <EventListContainer
          query={{ id: recentEventViews }}
          showCount={false}
          showLoadMore={false}
          source="home_page"
          carousel
        />
      </Container>
    </section>}

    {geoLocation &&
    <section className={popular}>
      <Container>
        <h2 className={heading}>Popular Events {geoLocation.city ? `near ${geoLocation.city}` : ''}</h2>
        <EventListContainer
          query={{ ...getLocalQuery(undefined, geoLocation), resultsPerPage: 6 }}
          showCount={false}
          showLoadMore={false}
          source="home_page"
          carousel
        />
      </Container>
    </section>}

    <section className={popular}>
      <Container>
        <h2 className={heading}>Trending Events</h2>
        <EventListContainer
          query={getLocalQuery(trendingQuery, getCountryViewport(countryCode))}
          showCount={false}
          showLoadMore={false}
          source="home_page"
        />
        <footer>
          <ButtonLink
            primary
            to={`/s?${stringifyQS(getLocalQuery(trendingQuery, getCountryViewport(countryCode)))}`}
            rel="nofollow"
          >
            See more
          </ButtonLink>
        </footer>
      </Container>
    </section>

    {!hasUserCookie &&
      <section className={signUpPrompt}>
        <Container>
          <SignUpPrompt
            title="Discover Events You Love"
            text="See the events that work for you based on your ability, fitness, location and preference. Sign up today."
            buttonText="Get Started"
            homepage
          />
        </Container>
      </section>
    }

    <section>
      <Container>
        <MemberBenefits columns />
      </Container>
    </section>

    <section>
      <Container className={partners}>
        <h2 className={heading}>Our official partners</h2>
        <ul className={partnerList}>
          <li>
            <Link to="/o/breca-swimrun-21520">
              <img src={`${MEDIA_ROOT}/images/partners/partner_breca_small.png`} alt="Breca Swimrun" />
            </Link>
          </li>
          <li>
            <Link to="/o/maverick-race-21986">
              <img src={`${MEDIA_ROOT}/images/partners/partner_maverick_small.png`} alt="Maverick Race" />
            </Link>
          </li>
          <li>
            <Link to="/o/f3-events-23642">
              <img src={`${MEDIA_ROOT}/images/partners/partner_f3_small.png`} alt="F3 Events" />
            </Link>
          </li>
          <li>
            <Link to="/o/virgin-sport-22056">
              <img src={`${MEDIA_ROOT}/images/partners/partner_virgin_small.png`} alt="Virgin Sport" />
            </Link>
          </li>
          <li>
            <Link to="/o/the-race-organiser-21669">
              <img src={`${MEDIA_ROOT}/images/partners/partner_raceorganiser_small.png`} alt="The Race Organiser" />
            </Link>
          </li>
          <li>
            <Link to="/o/runuk-65587">
              <img src={`${MEDIA_ROOT}/images/partners/partner_runuk_small.png`} alt="RunUK" />
            </Link>
          </li>
          <li>
            <Link to="/o/nice-work-33787">
              <img src={`${MEDIA_ROOT}/images/partners/partner_nicework_small.png`} alt="Nice Work" />
            </Link>
          </li>
          <li>
            <Link to="/o/tough-mudder-21416">
              <img src={`${MEDIA_ROOT}/images/partners/partner_tough_small.png`} alt="Tough Mudder" />
            </Link>
          </li>
          <li>
            <Link to="/o/spartan-21417">
              <img src={`${MEDIA_ROOT}/images/partners/partner_spartan_small.png`} alt="Spartan" />
            </Link>
          </li>
          <li>
            <Link to="/o/red-bull-22457">
              <img src={`${MEDIA_ROOT}/images/partners/partner_redbull_small.png`} alt="Red Bull" />
            </Link>
          </li>
          <li>
            <Link to="/o/threshold-sports-22071">
              <img src={`${MEDIA_ROOT}/images/partners/partner_threshold_small.png`} alt="Threshold" />
            </Link>
          </li>
          <li>
            <Link to="/o/active-training-world-36252">
              <img src={`${MEDIA_ROOT}/images/partners/partner_atw_small.png`} alt="Active Training World" />
            </Link>
          </li>
        </ul>
      </Container>
    </section>

    <HelpPrompt message="You need support to complete any event. We have an incredible team, from Olympic champions to people just completing their first 5ks, who can support you one on one." />
    <DiscoverFooter />
  </div>
);

Homepage.propTypes = {
  hasUserCookie: PropTypes.bool,
  countryCode: PropTypes.string,
  geoLocation: PropTypes.shape({
    city: PropTypes.string,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  }),
  trendingQuery: PropTypes.shape(),
};

Homepage.defaultProps = {
  countryCode: undefined,
  hasUserCookie: false,
  geoLocation: null,
  trendingQuery: {},
};


// Selectors are used so state shape is encapsulated within reducers
const mapStateToProps = state => ({
  countryCode: state.countryCode,
});

// Wrap in [react-redux connect](https://github.com/reactjs/react-redux/blob/master/docs/api.md#connect)
const HomepageWithRedux = connect(mapStateToProps)(Homepage); // eslint-disable-line no-class-assign, max-len

export default HomepageWithRedux;



// WEBPACK FOOTER //
// ./app/src/components/Homepage/index.jsx